import {A, E} from './util.mjs'
import * as sr from './shims/react.mjs'
import * as a from '@mitranim/js/all.mjs'
import * as dg from '@mitranim/js/dom_global'
import * as dr from '@mitranim/js/dom_reg.mjs'
import * as od from '@mitranim/js/obs_dom.mjs'
import * as c from './conf.mjs'
import * as n from './net.mjs'
import * as u from './util.mjs'

export class Link extends MixAnalytElem(u.analytViewObsStrict, u.MixElem(dg.global.HTMLAnchorElement)) {
  static customName = `a-link`
  static {dr.reg(this)}

  /*
  `HTMLAnchorElement` doesn't support the `disabled` property, but we really
  want to use it, like on buttons. So we fake it. The element still receives
  focus, which is inconsistent with disabled buttons. May revise.
  */
  get disabled() {return this.hasAttribute(`disabled`)}

  set disabled(val) {
    if (val) this.setAttribute(`disabled`, ``)
    else this.removeAttribute(`disabled`)
  }

  connectedCallback() {
    super.connectedCallback?.()
    this.onclick = this.onClick
  }

  onClick(eve) {
    if (u.eventHasDefault(eve, this)) return undefined
    if (this.disabled) return a.eventKill(eve)
    return this.onClickTransitionOpt(eve)
  }

  isSubpath(link) {return a.isSubpath(link, this.pathname)}

  isLocal() {
    return (
      this.origin === window.location.origin &&
      (!this.target || this.target === `_self`)
    )
  }

  onClickTransitionOpt(eve) {
    /*
    On hash-only changes, no action is needed. The browser will scroll to the
    element with a matching id, if any. We use that behavior on some pages.
    */
    if (u.isLocHashChange(window.location, this)) return undefined

    /*
    Browsers have built-in features such as: ctrl/cmd+click opens a new tab,
    alt+click downloads the target, etc. We want to preserve those default
    behaviors. Our links should only replace regular transitions for the
    current page.

    For comparison, YouTube's fake links do not handle modified clicks. They
    haven't fixed it in YEARS.
    */
    if (a.isEventModified(eve)) return undefined

    if (!this.isLocal()) return undefined

    // The method may be missing if analyt tracking is disabled.
    this.onClickAnalyt?.()
    return this.onClickTransition(eve)
  }

  onClickTransition(eve) {
    const ctx = this.ctx
    if (!ctx) return undefined

    a.eventKill(eve)
    this.onClickAnalyt?.()

    const opt = `noscroll` in this.dataset ? {noscroll: true} : undefined
    if (`replace` in this.dataset) return ctx.env.locReplace(this.href, opt)
    return ctx.env.locPush(this.href, opt)
  }
}

function MixDetails(cls) {
  return class MixDetailsCls extends cls {
    autoClose() {return this.hasAttribute(`data-auto-close`)}
    autoClosing() {return this.setAttribute(`data-auto-close`, ``), this}
    isOpen() {throw u.errImplementInSubclass()}
    setOpen() {throw u.errImplementInSubclass()}
    close() {this.setOpen(false)}
    onOpen() {if (this.autoClose()) this.listen()}
    onClose() {this.unlisten()}
    appendSummary() {throw u.errImplementInSubclass()}

    // TODO: consider optionally closing on Escape.
    connectedCallback() {
      this.onclick = a.eventStop
      this.onkeydown = a.eventStop
      if (this.isOpen()) this.onOpen()
    }

    disconnectedCallback() {this.unlisten()}

    /*
    Caution: these listeners must use capture, because they need to examine
    ancestor-descendant hierarchies before any other code modifies the DOM in
    response to these events.
    */
    listen() {
      document.addEventListener(`focus`, this, u.CAPTURE)
      document.addEventListener(`click`, this, u.CAPTURE)
    }

    unlisten() {
      document.removeEventListener(`focus`, this, u.CAPTURE)
      document.removeEventListener(`click`, this, u.CAPTURE)
    }

    handleEvent(event) {
      const {target} = event
      if (!this.isOpen()) return
      if (!u.isAncestorOf(this, target)) this.close()
    }
  }
}

export class DetailsNative extends MixDetails(u.MixElem(dg.global.HTMLDetailsElement)) {
  static {dr.reg(this)}

  init(props) {return E(this, A.with(props).cls(`details-native --tooltip`))}
  isOpen() {return a.reqBool(this.open)}
  setOpen(val) {return this.open = a.reqBool(val), this}
  hasBody() {return a.some(this.childNodes, isDetailsBodyNode)}
  summary() {return a.find(this.childNodes, u.isSummary)}

  appendSummary(props, ...chi) {
    this.appendChild(
      E(`summary`, A.with(props).cls(`details-summary`), ...chi),
    )
    return this
  }

  connectedCallback() {
    this.ontoggle = this.onToggle
    super.connectedCallback()
  }

  onToggle(eve) {
    a.eventStop(eve)
    if (this.open) this.onOpen(eve)
    else this.onClose(eve)
  }
}

// Note: `Text` is a valid body node.
function isDetailsBodyNode(val) {
  return a.isNode(val) && !u.isComment(val) && !u.isSummary(val)
}

/*
Alternative to `<details>` that uses a hidden `input[type=checkbox]`.

Note: prefer `DetailsNative` whenever possible.

Motive: having better control over the styling, particularly around the open /
closed states and whether the children are visible. Sometimes we want to use
media queries to expand a "details" by default on desktop, but keep it hidden
by default on mobile. With the native `<details>`, this seems to be impossible
to implement without relying on JS, which would either cause layout jumping or
make the control unresponsive until JS is loaded or if it's disabled. This
custom implementation allows us to implement such cases "cleanly".
*/
export class DetailsCustom extends MixDetails(u.Elem) {
  static {dr.reg(this)}

  init(props) {return E(this, props)}
  input() {return this.childNodes[0]} // Should always be `<input>`.
  label() {return this.childNodes[1]} // Should always be `<label>`.
  isOpen() {return !!this.input()?.checked}

  setOpen(val) {
    const input = this.input()
    if (input) input.checked = a.laxBool(val)
    return this
  }

  appendSummary(props, ...chi) {
    const id = u.weaklyRandomString()
    this.append(
      E(`input`, A.id(id).type(`checkbox`).hidden(true)),
      E(`label`, A.with(props).for(id).cls(`details-summary`), ...chi),
    )
    return this
  }

  connectedCallback() {
    this.onchange = this.onChange
    super.connectedCallback()
  }

  onChange(eve) {
    const tar = eve?.target
    if (tar !== this.input()) return
    a.eventStop(eve)
    if (tar.checked) this.onOpen(eve)
    else this.onClose(eve)
  }
}

export class GlobalLoader extends od.MixReac(u.Elem) {
  static customName = `global-loader`
  static {dr.reg(this)}
  run() {this.hidden = !(u.getEnv()?.transitionObs.val > 0)}
}

export class Notice extends u.Elem {
  connectedCallback() {
    this.addEventListener(c.EVENT_NOTICE_HIDE, this)
  }

  disconnectedCallback() {
    this.removeEventListener(c.EVENT_NOTICE_HIDE, this)
  }

  handleEvent(eve) {
    if (eve.type === c.EVENT_NOTICE_HIDE) {
      a.eventKill(eve)
      this.remove()
    }
  }
}

export const BTN_PROPS_NOTICE_HIDE = Object.freeze({
  is: `a-btn`,
  'data-event': c.EVENT_NOTICE_HIDE,
})

export class OneTimeNotice extends u.Elem {
  static customName = `one-time-notice`
  static {dr.reg(this)}

  key() {return this.dataset.key}
  bits() {return a.natOpt(this.dataset.bits)}
  // missing() {return a.vac(!this.env.disk.bank().hasBits(this.bits())) && this}

  connectedCallback() {
    this.addEventListener(c.EVENT_ONE_TIME_CLOSE, this)
  }

  disconnectedCallback() {
    this.removeEventListener(c.EVENT_ONE_TIME_CLOSE, this)
  }

  close() {
    const bank = this.env.disk.bank()
    const key = this.key()
    const bits = this.bits()

    if (key) bank.set(key, true)
    if (bits) bank.bitsAdd(bits)
    bank.write()
    this.hidden = true
  }

  handleEvent(eve) {
    if (eve.type === c.EVENT_ONE_TIME_CLOSE) {
      a.eventKill(eve)
      this.close()
    }
  }
}

export class LiveDateTime extends u.Elem {
  static customName = `live-date-time`
  static {dr.reg(this)}

  connectedCallback() {
    this.deinit()

    const date = `date` in this.dataset
    const time = `time` in this.dataset
    const fun = u.dateFormatFun(date, time)
    if (!fun) return

    const update = () => {this.setText(fun(Date.now()))}
    this.timer = setInterval(update, a.MS_IN_SEC)
  }

  disconnectedCallback() {this.deinit()}

  deinit() {clearInterval(this.timer)}
}

export class FakeLink extends u.Elem {
  static {dr.reg(this)}
  connectedCallback() {u.PropBui.fakeLink(this)}
}

/*
Short for "notification". Sticky-positioned popup for errors or congrats.
Method naming "show/close" matches `HTMLDialogElement`.
TODO consolidate with `errNoticeShow`.
*/
export class Notif extends u.Elem {
  static customName = `a-notif`
  static {dr.reg(this)}

  get A() {return A.cls(`notices`).id(this.constructor.id())}

  init() {return E(this, this.A)}

  show() {
    this.constructor.close()
    return document.body.appendChild(this)
  }

  async removeAfter(timer) {
    await timer
    this.remove()
  }

  static id() {return c.NOTIF_ID}
  static get() {return u.elemById(this.id())}
  static close() {this.get()?.remove()}

  static show(...chi) {
    return a.vac(chi) && E(new this(), this.prototype.A, chi).show()
  }

  static err(err) {
    if (!err) return this.close()
    return this.show(ErrToast(err))
  }

  static saved() {return this.succ(`Saved!`)}
  static deleted() {return this.dang(`Deleted!`)}

  static succ(...chi) {
    return this
      .show(E(`div`, A.cls(c.CLS_TOAST_SUCCESS), ...chi))
      .removeAfter(a.after(c.DELAY_POPUP_SUCCESS))
  }

  static dang(...chi) {
    return this
      .show(E(`div`, A.cls(c.CLS_TOAST_ERROR), ...chi))
      .removeAfter(a.after(c.DELAY_POPUP_SUCCESS))
  }
}

// TODO consolidate with `onUnhandledErr`.
export function onErr(err) {Notif.err(err)}

export function ErrToast(err) {
  if (c.DOM_AVAILABLE) u.maybeLogErr(err)

  return E(`div`, A.cls(c.CLS_TOAST_ERROR),
    E(`p`, A.cls(`wei-semi-bold`), `Sorry! Something went wrong`),
    E(`pre`, {}, a.show(err) || `unknown error`),
  )
}

/*
Tool for elements with custom "onclick" or "onchange" functionality, possibly
async. Features:

  * Counts concurrent invocations, prevents redundancies.
  * Disables for the duration of the call.
  * By default, reports errors via global toast.

Usage:

  * Subclass.
  * Override `.perform()`.
  * Override `.setDisabled()`.
  * Call the `.exec()` method in the appropriate native callback.

TODO move to `util-view.mjs`. This does not depend on any element classes and
does not define any view markup.
*/
export function MixExecable(val) {return MixExecableCache.goc(val)}

export class MixExecableCache extends a.DedupMixinCache {
  static make(cls) {
    return class MixExecableCls extends cls {
      /*
      Implementation note. Would prefer to use a private field here, but our
      bundler seems to have a bug that breaks private fields in mixins.
      */
      getObs() {return this[a.reqSym(u.obsKey)]}
      gocObs() {return this[a.reqSym(u.obsKey)] ??= new u.TaskObs()}

      /*
      TODO better name. Not called "run" to avoid collision with an interface
      used by `@mitranim/js/obs_dom.mjs`.
      */
      exec(eve) {
        a.eventKill(eve)
        if (this.isDisabled()) return undefined
        const obs = this.gocObs()
        return obs.task || obs.setTask(this.execAsync(eve))
      }

      async execAsync(eve) {
        await this.onErr()

        // This may disable the current element and/or its descendants.
        this.incCounter()

        try {
          await this.perform(eve)
          return true
        }
        catch (err) {
          await this.onErr(err)
          return false
        }
        finally {this.decCounter()}
      }

      /*
      Override in subclass. TODO better name. Not called "run" to avoid
      collision with an interface used by `@mitranim/js/obs_dom.mjs`.
      */
      perform() {}

      async onErr(err) {return u.onUnhandledErr(err)}

      getCounter() {return a.laxNat(this.counter)}
      addCounter(val) {this.setCounter(this.getCounter() + a.reqInt(val))}
      incCounter() {this.addCounter(+1)}
      decCounter() {this.addCounter(-1)}

      setCounter(next) {
        next = a.laxNat(next)
        const prev = this.getCounter()
        this.counter = next
        const prevDisabled = prev > 0
        const nextDisabled = next > 0
        if (prevDisabled !== nextDisabled) this.setDisabled(nextDisabled)
        return this
      }

      isDisabled() {
        return !!(this.disabled || this.readOnly || this.getCounter() > 0)
      }

      // Override in subclass.
      setDisabled(val) {this.toggleAttribute(`disabled`, a.reqBool(val))}
    }
  }
}

export function MixExecableFakeBtn(val) {return MixExecableFakeBtnCache.goc(val)}

export class MixExecableFakeBtnCache extends a.DedupMixinCache {
  static make(cls) {
    return class MixExecableFakeBtnCls extends MixExecable(cls) {
      connectedCallback() {
        super.connectedCallback?.()

        /*
        Modification of `role` and `tabIndex` is duplicated from
        `PropBui..fakeBtn`. Doing this in `connectedCallback` simplifies
        subclassing and ensures that these properties are only set when the
        custom element is actually interactive.
        */
        this.role ??= `button`
        if (this.tabIndex < 0) this.tabIndex = 0

        this.onclick ??= this.onClick
        this.onkeydown ??= this.onKeydown
      }

      onClick(eve) {this.exec(eve)}
      onKeydown(eve) {if (eve.key === c.KEY_ENTER) this.exec(eve)}
    }
  }
}

export class Iframe extends u.MixElem(dg.global.HTMLIFrameElement) {
  connectedCallback() {window.addEventListener(`message`, this)}

  disconnectedCallback() {window.removeEventListener(`message`, this)}

  handleEvent(eve) {
    if (eve.type === `message` || eve.source === this.contentWindow) {
      this.onMessage(eve)
    }
  }

  srcOrigin() {return a.vac(a.url(this.src).origin)}

  // Override in subclass.
  onMessage() {}

  /*
  https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage

  Sending the origin is necessary for embedded Vimeo videos, and may also be
  necessary in some other cases.
  */
  postMessage(val) {this.contentWindow?.postMessage(val, this.srcOrigin())}
}

/*
Experimental tool for custom elements whose content is initialized ONLY on the
client, lazily and asynchronously.
*/
export class Loader extends u.Elem {
  static {dr.reg(this)}

  init(src) {
    this.initLoading().initAsync(src).catch(u.onUnhandledErr)
    return this
  }

  initLoading() {
    return E(this, A.cls(`fg-warm-gray`), `Loading...`)
  }

  async initAsync(src) {
    try {
      this.replaceWith(u.nodeOnlyNotEmpty(u.ren.node(await src)))
    }
    catch (err) {
      this.replaceWith(ErrDisplay(err))
    }
  }
}

/*
Ideally we would consider using `ErrorText`. We use a locally hardcoded
component here to avoid a possible failing import, and because the other
component might be too large for this use case.
*/
function ErrDisplay(err) {
  return a.vac(err) && E(`span`, A.cls(`form-error`), err)
}

/*
Allows to embed Preact components into Prax elements. Supports SSR pre-rendering
and DOM rendering on the client, with automatic initialization of existing
elements after loading from SSR.

Important limitation: this can only render children. In the resulting DOM, this
element is the parent, and the React component is responsible for its children,
and can't modify the attributes of this parent element. Its attributes may be
set via `.initProps` or directly calling `.props` when including this into
outer markup.

For usage examples, see existing callsites.
*/
export function MixElemReactToPrax(val) {return MixElemReactToPraxCache.goc(val)}

class MixElemReactToPraxCache extends a.DedupMixinCache {
  static make(cls) {
    return class MixElemReactToPraxCls extends u.MixElemErrant(cls) {
      onConnected() {return this.reactRenderAsync()}
      disconnectedCallback() {u.preactClear(this)}

      /*
      Note: since this is a subclass of `HTMLElement`, by default this has the
      CSS property `display: inline`. For us, it's more convenient if block is
      the default.
      */
      get A() {return super.A ?? A.cls(`block`)}
      init() {return E(this, this.A)}
      initProps(props) {return E(this, this.A).setProps(props)}

      // Does NOT require `.constructor.preinit`.
      async initAsync() {
        return this.shouldDelayRender() ? this : this.reactRenderAsync()
      }

      // Requires `.constructor.preinit` or a synchronous override of
      // `.constructor.getComp`.
      initSync() {
        return this.shouldDelayRender() ? this : this.reactRenderSync()
      }

      // Override in subclass. May be synchronous or asynchronous.
      static getComp() {throw u.errImplementInSubclass()}

      // Enables `.initSync` if `.getComp` is asynchronous or requires prefetch.
      static async preinit(ctx) {
        this.comp = a.reqFun(await this.getComp())
        await this.comp.prefetch?.(ctx)
      }

      /*
      Implementation note.

      This element should render its React component in exactly two situations:

        * Pre-rendering HTML in SSR.
        * `.connectedCallback` on the client.

      On the client, this element should render its React component ONLY when
      connected to the DOM, and should NOT render during pushstate page
      transitions. We rely on `.disconnectedCallback` to properly unmount the
      component, performing cleanup such as destroying observable subscriptions.

      This method should be called during SSR and pushstate transition, and
      determines whether we should avoid full rendering at this time.
      */
      shouldDelayRender() {return c.DOM_AVAILABLE}

      async reactRenderAsync() {
        const vnode = await u.viewComponentToVirtualElement(
          this.ctx,
          await this.constructor.getComp(),
          await this.getProps(),
        )
        return this.reactChi(vnode)
      }

      // Requires `.constructor.preinit` or a synchronous override of
      // `.constructor.getComp`.
      reactRenderSync() {
        const comp = this.constructor.comp || this.constructor.getComp()
        if (a.isPromise(comp)) {
          throw TypeError(`${this.constructor.name}..reactRenderSync requires synchronous access to component`)
        }
        return this.reactChi(sr.E(comp, this.getProps()))
      }
    }
  }
}

/*
Similar to `MixElemReactToPrax`, but renders the React component only when
the element intersects the viewport. Currently supports only sync mode.
*/
export class ElemPraxToReactVis extends u.MixElemVisObs(u.Elem) {
  get A() {return A.cls(`block`)}

  init(props) {return E(this, this.A).setProps(props)}

  onVisible() {
    try {
      if (!this.hasChildNodes()) {
        this.reactChi(sr.E(this.getComp(), this.getProps()))
      }
    }
    catch (err) {
      this.replaceWith(ErrDisplay(err))
    }
  }

  // Override in subclass.
  getComp() {throw u.errImplementInSubclass()}
}

export class Quiz extends MixElemReactToPrax(u.Elem) {
  static {dr.reg(this)}
  static async getComp() {return (await u.imp(import(`./views/quiz.mjs`))).Quiz}
}

export class QuestionnaireForm extends MixElemReactToPrax(u.Elem) {
  static {dr.reg(this)}
  static async getComp() {return (await u.imp(import(`./views/main-page.mjs`))).QuestionnaireForm}
}

export class QuestionForm extends MixElemReactToPrax(u.Elem) {
  static {dr.reg(this)}
  static async getComp() {return (await u.imp(import(`./views/question-form.mjs`))).QuestionForm}
}

export class QuestionFormForAdvertisers extends MixElemReactToPrax(u.Elem) {
  static {dr.reg(this)}
  static async getComp() {return (await u.imp(import(`./views/advertisers.mjs`))).QuestionFormForAdvertisers}
}

export class FilterWithModal extends MixElemReactToPrax(u.Elem) {
  static {dr.reg(this)}
  static async getComp() {return (await u.imp(import(`./views/filters.mjs`))).FilterWithModal}
  get A() {return A.cls(`col-sta-cen w-full`)}
}

export class PractFilters extends MixElemReactToPrax(u.Elem) {
  static {dr.reg(this)}
  static async getComp() {return (await u.imp(import(`./views/practitioners.mjs`))).PractFiltersCls}
  get A() {return A.cls(`col-sta-cen w-full`)}
}

export class CopyToClipboard extends MixElemReactToPrax(u.Elem) {
  static {dr.reg(this)}
  static async getComp() {return (await u.imp(import(`./views/misc.mjs`))).CopyToClipboard}
  get A() {return A.cls(`copy-to-clipboard-parent`)}
}

export class ReferPractitionerForm extends MixElemReactToPrax(u.Elem) {
  static {dr.reg(this)}
  static async getComp() {return (await u.imp(import(`./views/refer-a-practitioner.mjs`))).ReferPractitionerForm}
}

export class PromotionBuyButton extends MixElemReactToPrax(u.Elem) {
  static {dr.reg(this)}
  static async getComp() {return (await u.imp(import(`./views/my-acc-promotions.mjs`))).PromotionBuyButton}
}

export class MagicLogin extends MixElemReactToPrax(u.Elem) {
  static {dr.reg(this)}
  static async getComp() {return (await u.imp(import(`./views/auth-magic.mjs`))).MagicLogin}
  get A() {return A.cls(`col-sta-cen w-full`)}
}

export class FestivePromoLogin extends MagicLogin {
  static {dr.reg(this)}
  static async getComp() {return (await u.imp(import(`./views/festive-promo.mjs`))).FestivePromoLogin}
  get A() {return A.cls(`festive-modal`)}
}

export class MembershipItem extends MixElemReactToPrax(u.Elem) {
  static {dr.reg(this)}
  static async getComp() {return (await u.imp(import(`./views/landings.mjs`))).MembershipItem}
}

export class SubscriptionPurchaseActiveInactive extends MixElemReactToPrax(u.Elem) {
  static {dr.reg(this)}
  static async getComp() {return (await u.imp(import(`./views/my-membership.mjs`))).SubscriptionPurchaseActiveInactive}
}

export class GiftCardPurchaseForm extends MixElemReactToPrax(u.Elem) {
  static {dr.reg(this)}
  static async getComp() {return (await u.imp(import(`./views/gift-card-purchase.mjs`))).GiftCardPurchaseForm}
}

export class PaymentSystemConnectStripe extends MixElemReactToPrax(u.Elem) {
  static {dr.reg(this)}
  static async getComp() {return (await u.imp(import(`./views/my-acc-wallet.mjs`))).PaymentSystemConnectStripe}
}

export class SessionReviewForm extends MixElemReactToPrax(u.Elem) {
  static {dr.reg(this)}
  static async getComp() {return (await u.imp(import(`./views/review.mjs`))).SessionReviewForm}
}

export class UnsubscribeForm extends MixElemReactToPrax(u.Elem) {
  static {dr.reg(this)}
  static async getComp() {return (await u.imp(import(`./views/unsubscribe.mjs`))).UnsubscribeForm}
}

// Placed in a generic elements file becaused it's used on most pages.
export class CatMultiMenu extends u.MixElemVisObs(u.Elem) {
  static {dr.reg(this)}

  onVisible() {
    if (!this.hasChildNodes()) this.appendChild(Loader.init(this.initAsync()))
  }

  init() {return E(this, A.cls(`col-sta-sta w-full gap`))}

  setConCat(val) {
    this.conCat = val
    this.initAsync()
  }

  async initAsync() {
    const [conCatMain, btn, lh] = await u.plist()
      .add(this.ctx.dat.conCatMain.valueTask())
      .add(u.imp(import(`./views/buttons.mjs`)))
      .add(u.imp(import(`./views/layout-header.mjs`)))

    const conCat = this.conCat || conCatMain
    if (!conCat) return

    const parent = conCat.getParent()
    const children = conCat.getChildConCats()

    E(this, {},
      a.vac(parent) && E(`div`, A.cls(`row-bet-cen gap-sm`),
        btn.BackButton({onclick: this.setConCat.bind(this, parent)}),
        E(
          new Link(),
          A.href(conCat.sitePathDefault()).cls(`fg-black font-p wei-bold w-full anim hov-fg-accent`),
          conCat.title,
        ),
      ),
      a.vac(a.hasLen(children)) && E(`div`, A.cls(`col-sta-sta w-full mah-24 over-y-auto`),
        a.map(children, conCat => lh.MobileMenuButton(
          (
            a.vac(a.hasLen(conCat.getChildConCats()))
              ? {onclick: this.setConCat.bind(this, conCat)}
              : {href: conCat.sitePathDefault(), noArrow: true}
          ),
          conCat.title,
        )),
      ),
    )
  }
}

function MixAnalytElem(obs /* : IntersectionObserver? */, cls) {
  if (obs) {
    cls = class MixAnalytViewElem extends cls {
      connectedCallback() {
        super.connectedCallback?.()
        if (u.hasAnalytDataset(this)) obs.observe(this)
      }

      disconnectedCallback() {
        super.disconnectedCallback?.()
        obs.unobserve(this)
      }
    }
  }

  if (c.ENABLE_ACTS) {
    cls = class MixAnalytClickElem extends cls {
      connectedCallback() {
        super.connectedCallback?.()
        if (u.hasAnalytDataset(this)) this.onclick = this.onClickAnalyt
      }

      onClickAnalyt() {
        this.onClickAnalytAct ||= this.sendClickAct()
        this.onClickAnalytGtag ||= this.sendClickGtag()
      }

      async sendClickAct() {
        const inp = u.elemActInp(this)
        if (inp) {
          inp.type = c.ACT_TYPE_CLICK
          await n.createAct(this.ctx, inp)
        }
      }

      async sendClickGtag() {
        const inp = u.elemGtagInp(this)
        if (inp) this.env.gtag.sendEvent(...inp)
      }
    }
  }

  return cls
}

export class AnalytStrict extends MixAnalytElem(u.analytViewObsStrict, u.Elem) {
  /*
  Obfuscate the name to prevent the word "analytics" from blatantly appearing
  in the markup. Some content blockers intercept network requests based on
  blacklists including words such as "analytics" and "tracking", and it's
  possible that some also mess with the DOM based on similar detection rules.

  TODO better name.
  */
  static customName = `a-s`
  static {dr.reg(this)}
}

export class AnalytLax extends MixAnalytElem(u.analytViewObsLax, u.Elem) {
  /*
  Obfuscate the name to prevent the word "analytics" from blatantly appearing
  in the markup. Some content blockers intercept network requests based on
  blacklists including words such as "analytics" and "tracking", and it's
  possible that some also mess with the DOM based on similar detection rules.

  TODO better name.
  */
  static customName = `a-l`
  static {dr.reg(this)}
}

/*
Each option's value should be an id of an arbitrary element rendered outside
of this select. On any change, we show elements whose id is selected, and hide
elements whose id is not selected.

Known race condition: after SSR, if the user selects a different option BEFORE
our scripts are initialized, the select will be out of sync with the shown
element. The problem fixes itself if the user selects another option after the
scripts are initialized.
*/
export class SelectTabulator extends dg.global.HTMLSelectElement {
  static {dr.reg(this)}

  connectedCallback() {this.onchange = this.onChange}

  onChange() {
    for (const val of this.options) {
      u.elemToggleHidden(u.elemById(val.value), !val.selected)
    }
  }
}
